/**
 * Birth dates that are only partly known.
 *
 * Squad lists from real clubs do not all carry a full date of birth. A federation
 * export may give only the birth year; a handwritten register kept by a school
 * may give a month and a year; a player who arrived from another country may have
 * no day recorded anywhere. Youth football is organised by birth year, so the
 * year alone is already the most load-bearing part — refusing to store it, or
 * inventing 1 January to make the column fit, are both worse than admitting the
 * precision.
 *
 * A birth date is therefore stored as a *prefix* of an ISO date:
 *
 *   'YYYY-MM-DD'  exact
 *   'YYYY-MM'     month known, day not
 *   'YYYY'        year known, nothing finer
 *
 * The column is unchanged, every value already in it stays valid, and the
 * precision is readable from the length rather than tracked in a second field
 * that can disagree with it.
 */

export type DatePrecision = 'day' | 'month' | 'year';

/** The oldest birth year we will accept. Old enough for any coach on record. */
const MIN_YEAR = 1900;

/** How far into the future a birth year may plausibly sit (clock skew only). */
const FUTURE_YEARS = 1;

function currentYear(): number {
  return new Date().getUTCFullYear();
}

/**
 * Arabic-Indic and Extended Arabic-Indic digits to ASCII.
 *
 * Arabic is a launch language, and a spreadsheet typed in Arabic on Windows
 * often carries ٢٠٠٧ rather than 2007. Rejecting those rows would make the
 * importer useless in exactly the market it was translated for.
 */
export function normalizeDigits(value: string): string {
  return value.replace(/[٠-٩۰-۹]/g, (d) => {
    const code = d.codePointAt(0)!;
    const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
    return String(code - base);
  });
}

function stripDiacritics(value: string): string {
  // NFD then drop combining marks: 'février' → 'fevrier', 'آذار' → 'اذار'.
  return value.normalize('NFD').replace(/[̀-ًͯ-ْٰ]/g, '');
}

/**
 * Month names in the three launch languages, plus the Levantine set an Arabic
 * speaker in the Mashriq would write instead of the Maghrebi one. Matching is on
 * a prefix, so 'sept', 'septembre' and 'september' all land on 9.
 */
const MONTH_NAMES: readonly (readonly string[])[] = [
  ['jan', 'january', 'janvier', 'ينا', 'يناير', 'كانون الثاني'],
  ['feb', 'february', 'fev', 'fevrier', 'فبر', 'فبراير', 'شباط'],
  ['mar', 'march', 'mars', 'مار', 'مارس', 'اذار'],
  ['apr', 'april', 'avr', 'avril', 'ابر', 'ابريل', 'نيسان'],
  ['may', 'mai', 'ماي', 'مايو', 'ايار'],
  ['jun', 'june', 'juin', 'يون', 'يونيو', 'حزيران'],
  ['jul', 'july', 'juil', 'juillet', 'يول', 'يوليو', 'تموز'],
  ['aug', 'august', 'aou', 'aout', 'اغس', 'اغسطس', 'اب'],
  ['sep', 'sept', 'september', 'septembre', 'سبت', 'سبتمبر', 'ايلول'],
  ['oct', 'october', 'octobre', 'اكت', 'اكتوبر', 'تشرين الاول'],
  ['nov', 'november', 'novembre', 'نوف', 'نوفمبر', 'تشرين الثاني'],
  ['dec', 'december', 'decembre', 'ديس', 'ديسمبر', 'كانون الاول'],
];

/** 1–12, or null. Accepts a full name, an abbreviation or a number. */
export function monthFromName(raw: string): number | null {
  const word = stripDiacritics(normalizeDigits(raw).trim().toLowerCase()).replace(/\.$/, '');
  if (!word) return null;
  if (/^\d{1,2}$/.test(word)) {
    const n = Number(word);
    return n >= 1 && n <= 12 ? n : null;
  }
  for (let i = 0; i < MONTH_NAMES.length; i++) {
    for (const name of MONTH_NAMES[i]!) {
      // Either side may be the abbreviation: 'sep' matches 'september', and
      // 'septembre' matches the stored 'sept'.
      if (name.startsWith(word) || word.startsWith(name)) return i + 1;
    }
  }
  return null;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function plausibleYear(year: number): boolean {
  return year >= MIN_YEAR && year <= currentYear() + FUTURE_YEARS;
}

function two(n: number): string {
  return String(n).padStart(2, '0');
}

/** Builds a stored value, or undefined when the parts do not form a real date. */
function compose(year: number, month?: number, day?: number): string | undefined {
  if (!plausibleYear(year)) return undefined;
  if (month === undefined) return String(year);
  if (month < 1 || month > 12) return undefined;
  if (day === undefined) return `${year}-${two(month)}`;
  if (day < 1 || day > daysInMonth(year, month)) return undefined;
  return `${year}-${two(month)}-${two(day)}`;
}

/** Which parts of a stored value are known, or null if it is not one of ours. */
export function birthDatePrecision(value: string): DatePrecision | null {
  if (/^\d{4}$/.test(value)) return plausibleYear(Number(value)) ? 'year' : null;
  if (/^\d{4}-\d{2}$/.test(value)) {
    return compose(Number(value.slice(0, 4)), Number(value.slice(5, 7))) === value
      ? 'month'
      : null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const composed = compose(
      Number(value.slice(0, 4)),
      Number(value.slice(5, 7)),
      Number(value.slice(8, 10)),
    );
    return composed === value ? 'day' : null;
  }
  return null;
}

export function isBirthDate(value: string): boolean {
  return birthDatePrecision(value) !== null;
}

/**
 * Reads a birth date written in any of the ways a coach or a spreadsheet writes
 * one, and returns it at whatever precision was actually given.
 *
 * Day-first is assumed for all-numeric forms, because that is the convention in
 * every locale this ships in. Two ambiguous groups ('03/08') are refused rather
 * than guessed: the difference between March 2008 and August 2003 is a whole age
 * category, and a wrong answer here is worse than an unfilled cell.
 */
export function parseBirthDate(raw: string): string | undefined {
  const value = normalizeDigits(raw).trim().replace(/\s+/g, ' ');
  if (!value) return undefined;

  // Year alone. Four digits in a plausible birth-year range are a year, never an
  // Excel serial — a serial for anyone alive is five digits (1930 is 10958).
  if (/^\d{4}$/.test(value)) return compose(Number(value));

  // Excel serial day number.
  if (/^\d{5}$/.test(value)) {
    const serial = Number(value);
    if (serial > 1 && serial < 80_000) {
      const d = new Date(Date.UTC(1899, 11, 30) + serial * 86_400_000);
      return compose(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
    }
    return undefined;
  }

  // ISO and ISO-like, year first: 2008-03-14, 2008/03, 2008-3.
  const isoish = value.match(/^(\d{4})[-/.](\d{1,2})(?:[-/.](\d{1,2}))?$/);
  if (isoish) {
    return compose(
      Number(isoish[1]),
      Number(isoish[2]),
      isoish[3] === undefined ? undefined : Number(isoish[3]),
    );
  }

  // Day-first numeric: 14/03/2008, 14.3.08.
  const dayFirst = value.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (dayFirst) {
    let year = Number(dayFirst[3]);
    if (year < 100) year += year > 50 ? 1900 : 2000;
    return compose(year, Number(dayFirst[2]), Number(dayFirst[1]));
  }

  // Month and year only, month first: 03/2008, 3-2008.
  const monthYear = value.match(/^(\d{1,2})[-/. ](\d{4})$/);
  if (monthYear) return compose(Number(monthYear[2]), Number(monthYear[1]));

  // Anything with a month name. Split into tokens and classify rather than
  // matching an order: 'mars 2008', '14 mars 2008', 'March 14, 2008' and
  // '14 كانون الثاني 2008' are all the same three facts in a different sequence,
  // and one multi-word Arabic month name would defeat a positional pattern.
  const tokens = value.split(/[\s.,/\-]+/).filter(Boolean);
  const numbers = tokens.filter((tk) => /^\d{1,4}$/.test(tk));
  const words = tokens.filter((tk) => !/^\d+$/.test(tk)).join(' ');
  if (words && numbers.length >= 1 && numbers.length <= 2) {
    const month = monthFromName(words);
    if (month === null) return undefined;
    const year = numbers.find((n) => n.length === 4);
    if (year === undefined) return undefined;
    const rest = numbers.filter((n) => n !== year);
    if (rest.length > 1) return undefined;
    const day = rest[0] === undefined ? undefined : Number(rest[0]);
    return compose(Number(year), month, day);
  }

  return undefined;
}

export interface AgeEstimate {
  /** The age the player has certainly reached. */
  years: number;
  /** The oldest they could be, given what is known. Equals `years` when exact. */
  maxYears: number;
  precision: DatePrecision;
  /** True when the stored value cannot pin the age to a single number. */
  approximate: boolean;
}

/**
 * Age from a birth date of any precision.
 *
 * A partial date gives a range, not a number, and the range is reported rather
 * than collapsed. A player known only as "born 2007" is 18 or 19 today, and
 * showing a confident 18 would be a fabrication — small in isolation, and
 * exactly the kind that decides a selection for an age-capped competition.
 */
export function ageFromBirthDate(
  value: string | null | undefined,
  on: Date = new Date(),
): AgeEstimate | null {
  if (!value) return null;
  const precision = birthDatePrecision(value);
  if (precision === null) return null;

  const year = Number(value.slice(0, 4));
  const month = precision === 'year' ? 1 : Number(value.slice(5, 7));
  const day = precision === 'day' ? Number(value.slice(8, 10)) : 1;

  const earliest = Date.UTC(year, month - 1, day);
  const latest =
    precision === 'day'
      ? earliest
      : precision === 'month'
        ? Date.UTC(year, month - 1, daysInMonth(year, month))
        : Date.UTC(year, 11, 31);

  const ageAt = (birthMs: number): number => {
    const b = new Date(birthMs);
    let age = on.getUTCFullYear() - b.getUTCFullYear();
    const before =
      on.getUTCMonth() < b.getUTCMonth() ||
      (on.getUTCMonth() === b.getUTCMonth() && on.getUTCDate() < b.getUTCDate());
    if (before) age -= 1;
    return age;
  };

  // The later the birth date, the younger the player: latest → minimum age.
  const years = ageAt(latest);
  const maxYears = ageAt(earliest);
  if (maxYears < 0 || maxYears > 120) return null;
  return {
    years: Math.max(years, 0),
    maxYears,
    precision,
    approximate: years !== maxYears,
  };
}
